import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DropdownContext = createContext();

export function DropdownProvider({ children }) {
  const { token } = useAuth();
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchConfigs();
    }
  }, [token]);

  const fetchConfigs = async () => {
    try {
      const response = await axios.get(`${API}/settings/status-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfigs(response.data);
    } catch (error) {
      console.error("Failed to fetch dropdown configs", error);
      // Use empty object - will fall back to defaults in components
      setConfigs({});
    } finally {
      setLoading(false);
    }
  };

  // Fallback defaults for modules when not set
  const DEFAULTS = {
    projects: {
      type: [
        { value: "Retail", label: "Retail", color: "bg-blue-100 text-blue-700" },
        { value: "Corporate", label: "Corporate", color: "bg-purple-100 text-purple-700" },
        { value: "Residential", label: "Residential", color: "bg-emerald-100 text-emerald-700" },
        { value: "Hospitality", label: "Hospitality", color: "bg-amber-100 text-amber-700" },
        { value: "Healthcare", label: "Healthcare", color: "bg-red-100 text-red-700" },
        { value: "F&B", label: "F&B", color: "bg-orange-100 text-orange-700" }
      ],
      execution_model: [
        { value: "Turnkey", label: "Turnkey", color: "bg-blue-100 text-blue-700" },
        { value: "Design & Build", label: "Design & Build", color: "bg-purple-100 text-purple-700" },
        { value: "Consultancy", label: "Consultancy", color: "bg-slate-100 text-slate-700" }
      ],
      status: [
        { value: "Planning", label: "Planning", color: "bg-slate-100 text-slate-700" },
        { value: "In Progress", label: "In Progress", color: "bg-blue-100 text-blue-700" },
        { value: "On Hold", label: "On Hold", color: "bg-amber-100 text-amber-700" },
        { value: "Completed", label: "Completed", color: "bg-green-100 text-green-700" },
        { value: "Cancelled", label: "Cancelled", color: "bg-red-100 text-red-700" }
      ]
    },
    tasks: {
      status: [
        { value: "To Do", label: "To Do", color: "bg-slate-100 text-slate-700" },
        { value: "In Progress", label: "In Progress", color: "bg-blue-100 text-blue-700" },
        { value: "Review", label: "Review", color: "bg-amber-100 text-amber-700" },
        { value: "Completed", label: "Completed", color: "bg-green-100 text-green-700" },
        { value: "On Hold", label: "On Hold", color: "bg-red-100 text-red-700" }
      ],
      priority: [
        { value: "Low", label: "Low", color: "bg-slate-100 text-slate-700" },
        { value: "Medium", label: "Medium", color: "bg-yellow-100 text-yellow-700" },
        { value: "High", label: "High", color: "bg-orange-100 text-orange-700" },
        { value: "Critical", label: "Critical", color: "bg-red-100 text-red-700" }
      ]
    },
    expenses: {
      status: [
        { value: "Pending", label: "Pending", color: "bg-yellow-100 text-yellow-700" },
        { value: "Approved", label: "Approved", color: "bg-green-100 text-green-700" },
        { value: "Rejected", label: "Rejected", color: "bg-red-100 text-red-700" },
        { value: "Paid", label: "Paid", color: "bg-blue-100 text-blue-700" }
      ],
      payment_mode: [
        { value: "Cash", label: "Cash", color: "bg-emerald-100 text-emerald-700" },
        { value: "Bank Transfer", label: "Bank Transfer", color: "bg-blue-100 text-blue-700" },
        { value: "Cheque", label: "Cheque", color: "bg-purple-100 text-purple-700" },
        { value: "UPI", label: "UPI", color: "bg-indigo-100 text-indigo-700" },
        { value: "Card", label: "Card", color: "bg-orange-100 text-orange-700" }
      ]
    }
  };

  // Get options for a specific module and field
  const getOptions = (module, field) => {
    const options = configs[module]?.[field];
    if (options && options.length > 0) return options;
    // Return defaults if available
    return DEFAULTS[module]?.[field] || [];
  };

  // Get color class for a value
  const getColor = (module, field, value) => {
    const options = getOptions(module, field);
    const option = options.find(opt => opt.value === value);
    return option?.color || "bg-slate-100 text-slate-700";
  };

  // Refresh configs (call after admin makes changes)
  const refreshConfigs = () => {
    fetchConfigs();
  };

  return (
    <DropdownContext.Provider value={{ 
      configs, 
      loading, 
      getOptions, 
      getColor, 
      refreshConfigs 
    }}>
      {children}
    </DropdownContext.Provider>
  );
}

export function useDropdowns() {
  const context = useContext(DropdownContext);
  if (!context) {
    throw new Error("useDropdowns must be used within a DropdownProvider");
  }
  return context;
}

export default DropdownContext;
